/**
 * LEFeeRow - Individual fee line item for Loan Estimate viewer
 *
 * Displays a fee description with amount, supporting:
 * - Regular fee items
 * - Subtotals (bold)
 * - Totals (bold with top border)
 * - Indented nested items
 * - Optional detail text (e.g., "per day for X days")
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface LEFeeRowProps {
  description: string;
  feeName?: string;           // Separate fee name (e.g., "Settlement Fee")
  paidTo?: string;            // Separate payee name (e.g., "Escrow of the West")
  amount: number;
  detail?: string;
  isTotal?: boolean;
  isSubtotal?: boolean;
  indent?: boolean;
  negative?: boolean;
  className?: string;
  paidToType?: string;        // Lender, Broker, Other, etc. - shows circled letter
  aprAffecting?: boolean;     // Shows APR badge when true
  showMissingAlert?: boolean; // Show alert for missing data
}

/**
 * Format currency for display (always show cents)
 */
function formatCurrency(amount: number, negative?: boolean): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(amount));

  if (negative || amount < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

/**
 * Get the circled letter indicator for paid to type
 */
function getPaidToTypeIndicator(paidToType?: string): { letter: string; title: string } | null {
  if (!paidToType) {
return null;
}
  const type = paidToType.toLowerCase();
  if (type === 'lender') {
return { letter: 'L', title: 'Paid to Lender' };
}
  if (type === 'broker') {
return { letter: 'B', title: 'Paid to Broker' };
}
  if (type === 'other' || type === 'others') {
return { letter: 'O', title: 'Paid to Other' };
}
  if (type === 'seller') {
return { letter: 'S', title: 'Paid to Seller' };
}
  return null;
}

export const LEFeeRow: React.FC<LEFeeRowProps> = ({
  description,
  feeName,
  paidTo,
  amount,
  detail,
  isTotal = false,
  isSubtotal = false,
  indent = false,
  negative = false,
  className,
  paidToType,
  aprAffecting,
  showMissingAlert = false
}) => {
  const isBold = isTotal || isSubtotal;
  const typeIndicator = getPaidToTypeIndicator(paidToType);

  // Determine if fee name is missing (when description starts with "Line")
  const hasMissingFeeName = showMissingAlert && !feeName && description.startsWith('Line');
  // Determine if paid to type is missing when there's a payee
  const hasMissingPaidToType = showMissingAlert && !paidToType && paidTo;

  return (
    <div
      className={cn(
        'flex items-start justify-between py-1.5 text-sm',
        isTotal && 'border-t pt-2 mt-2',
        indent && 'pl-4',
        className
      )}
    >
      <div className="flex-1 min-w-0 pr-4 flex items-start gap-2">
        {typeIndicator && (
          <span
            title={typeIndicator.title}
            className="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-slate-400 text-[10px] font-bold text-slate-600 dark:border-slate-500 dark:text-slate-400 flex-shrink-0 mt-0.5"
          >
            {typeIndicator.letter}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Fee name with optional missing alert */}
            <span
              className={cn(
                'text-foreground',
                isBold && 'font-semibold',
                indent && 'text-muted-foreground'
              )}
            >
              {feeName || description}
            </span>

            {/* Missing fee name alert */}
            {hasMissingFeeName && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                Missing Name
              </span>
            )}

            {/* Paid to separator + name */}
            {paidTo ? (
              <>
                <span className="text-muted-foreground">→</span>
                <span className="text-muted-foreground text-sm">{paidTo}</span>
              </>
            ) : showMissingAlert && (
              <>
                <span className="text-muted-foreground">→</span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                  Payee not specified
                </span>
              </>
            )}

            {/* Missing paidToType alert (only when paidTo exists but no L/O indicator) */}
            {hasMissingPaidToType && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                Missing L/O
              </span>
            )}
          </div>
          {detail && (
            <span className="block text-xs text-muted-foreground mt-0.5">
              {detail}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {aprAffecting && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
            APR
          </span>
        )}
        <span
          className={cn(
            'text-right whitespace-nowrap tabular-nums',
            isBold && 'font-semibold',
            isTotal && 'text-base'
          )}
        >
          {formatCurrency(amount, negative)}
        </span>
      </div>
    </div>
  );
};

/**
 * Fee section header with total
 */
interface LEFeeSectionHeaderProps {
  label: string;
  total: number;
  isExpanded?: boolean;
  onToggle?: () => void;
}

export const LEFeeSectionHeader: React.FC<LEFeeSectionHeaderProps> = ({
  label,
  total
}) => {
  return (
    <div className="flex items-center justify-between py-2 text-sm bg-muted/30 px-3 rounded -mx-3">
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{formatCurrency(total)}</span>
    </div>
  );
};

/**
 * Fee items group with expandable detail
 */
interface LEFeeGroupProps {
  label: string;
  total: number;
  items: Array<{
    description: string;
    feeName?: string;
    paidTo?: string;
    amount: number;
    detail?: string;
    paidToType?: string;
    aprAffecting?: boolean;
  }>;
  defaultExpanded?: boolean;
  showMissingAlert?: boolean;
  encompassTotal?: number;  // Encompass value for validation comparison
  showValidation?: boolean; // Whether to show green/red validation ring
  totalWithoutDiscount?: number; // Section A only: use this value for validation instead of total
}

export const LEFeeGroup: React.FC<LEFeeGroupProps> = ({
  label,
  total,
  items,
  defaultExpanded = false,
  showMissingAlert = false,
  encompassTotal,
  showValidation = false,
  totalWithoutDiscount
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Check if all fees in this section are APR-affecting
  const allAprAffecting = items.length > 0 && items.every(item => item.aprAffecting === true);

  // Validation: compare calculated total with Encompass total (allow penny rounding)
  // For Section A, use totalWithoutDiscount if provided (NEWHUD.X686 excludes Loan Discount)
  const isValidated = showValidation && encompassTotal !== undefined;
  const valueToCompare = totalWithoutDiscount !== undefined ? totalWithoutDiscount : total;
  const isMatch = isValidated ? Math.abs(valueToCompare - encompassTotal) < 0.01 : null;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-2 text-sm hover:bg-muted/30 px-2 -mx-2 rounded transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{label}</span>
          {allAprAffecting && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              All fees affect APR
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={cn(
            "font-semibold tabular-nums",
            isValidated && "px-2 py-0.5 rounded",
            isMatch === true && "ring-2 ring-green-500 bg-green-50 dark:bg-green-900/20",
            isMatch === false && "ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20"
          )}>{formatCurrency(total)}</span>
          <svg
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              expanded && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>
      {expanded && items.length > 0 && (
        <div className="pl-4 space-y-0.5 border-l-2 border-muted ml-2">
          {items.map((item, idx) => (
            <LEFeeRow
              key={idx}
              description={item.description}
              feeName={item.feeName}
              paidTo={item.paidTo}
              amount={item.amount}
              detail={item.detail}
              paidToType={item.paidToType}
              aprAffecting={item.aprAffecting}
              showMissingAlert={showMissingAlert}
              indent
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LEFeeRow;
